import * as React from "react";
import { NavMenuOption, PageLayout } from "components/PageLayout/PageLayout";
import { BreadcrumbItem } from "components/Breadcrumbs/Breadcrumbs";
import Box from "@mui/material/Box";
import { AiServiceUsageReportTable } from "./AiServiceUsageReportTable/AiServiceUsageReportTable";
import { Typography } from "@mui/material";
import { useSessionStorage } from "usehooks-ts";
import { SelectOption } from "@mui/base";
import { Filter } from "./Filter/Filter";
import { ExportAnalyticsButton } from "./ExportAnalyticsButton/ExportAnalyticsButton";
import { useQuery } from "react-query";
import { AiServiceUsageReport, ApiError, MetricsService } from "gen/clients";

const breadcrumbs: BreadcrumbItem[] = [
  {
    path: "/analytics",
    label: "Analytics"
  },
  {
    path: "/analytics/aiServiceUsageAnalytics",
    label: "AI Service Usage Analytics",
    active: true
  }
];

const AI_SERVICE_USAGE_SESSION_STATE_VAR_NAME = "aiServiceUsagePageStateV1";

const AiServiceUsageAnalyticsPage: React.FC = () => {
  const now = new Date();
  const [from, setFrom] = React.useState<Date>(new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000));
  const [to, setTo] = React.useState<Date>(now);
  const [isSearching, setIsSearching] = React.useState(false);
  const [client, setClient] = useSessionStorage<SelectOption<string>>(AI_SERVICE_USAGE_SESSION_STATE_VAR_NAME, {
    label: "",
    value: ""
  });
  const [pageToken, setPageToken] = React.useState<string | undefined>();
  const [nextPageToken, setNextPageToken] = React.useState<string | undefined>();
  const [rowsPerPage, setRowsPerPage] = React.useState(20);
  const paginatorResetRef = React.useRef<() => void>(() => {});

  const { data, isFetching, error } = useQuery<AiServiceUsageReport, ApiError>(
    ["getAiServiceUsageReport", client.value, from, to, pageToken, rowsPerPage],
    {
      queryFn: () =>
        MetricsService.getAiServiceUsageReport({
          clientId: client.value as string,
          fromDate: from.toISOString(),
          toDate: to.toISOString(),
          pageToken,
          pageSize: rowsPerPage
        }),
      cacheTime: 0,
      enabled: !!client.value,
      onSuccess: collection => {
        setNextPageToken(collection.nextPageToken);
        setIsSearching(false);
      },
      onError: () => {
        setIsSearching(false);
      }
    }
  );

  const onFilterChange = React.useCallback(
    (newFrom: Date, newTo: Date, newClient: SelectOption<string>) => {
      setFrom(newFrom);
      setTo(newTo);
      setClient(newClient);
      setPageToken(undefined);
      setNextPageToken(undefined);
      setIsSearching(true);
      paginatorResetRef?.current();
    },
    [setClient]
  );

  const onRowsPerPageChange = React.useCallback((newRowsPerPage: number) => {
    setRowsPerPage(newRowsPerPage);
    setPageToken(undefined);
    setNextPageToken(undefined);
    paginatorResetRef?.current();
  }, []);

  React.useEffect(() => {
    if (!client.value) {
      setIsSearching(false);
    }
  }, [client.value]);

  return (
    <PageLayout
      selectedNavMenu={NavMenuOption.ANALYTICS}
      title={
        <Typography variant="h6" sx={{ mb: 2 }}>
          AI Service Usage Analytics
        </Typography>
      }
      breadcrumbs={breadcrumbs}
    >
      <Filter from={from} to={to} client={client} isSearching={isSearching} onChange={onFilterChange} />

      {client.value && (
        <Box>
          <Box textAlign="right">
            <ExportAnalyticsButton clientId={client.value} from={from} to={to} />
          </Box>
          <AiServiceUsageReportTable
            data={data}
            isLoading={isFetching}
            error={error}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={onRowsPerPageChange}
            onPageTokenChange={setPageToken}
            nextPageToken={nextPageToken}
            paginatorResetRef={paginatorResetRef}
          />
        </Box>
      )}
    </PageLayout>
  );
};

export { AiServiceUsageAnalyticsPage };
